import { useCallback, useEffect, useRef, useState } from "react"
import { fetchImages, type ImageSearchResult } from "../api/imageSearch"
import { errorMessage } from "../types"

const PAGE_SIZE = 12

export function useImageSearch(initialQuery: string) {
  const [startingQuery] = useState(initialQuery)
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<ImageSearchResult[]>([])
  const [status, setStatus] = useState("")
  const request = useRef(0)
  const searches = useRef(new Map<string, Promise<ImageSearchResult[]>>())
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const search = useCallback(async (value: string) => {
    const current = ++request.current
    const normalizedQuery = value.trim()
    setVisibleCount(PAGE_SIZE)
    setResults([])
    if (!normalizedQuery) {
      setStatus("Enter a product name to search.")
      return
    }
    setStatus("Searching for pictures…")
    try {
      let pending = searches.current.get(normalizedQuery)
      if (!pending) {
        pending = fetchImages(normalizedQuery).catch((error) => {
          searches.current.delete(normalizedQuery)
          throw error
        })
        searches.current.set(normalizedQuery, pending)
      }
      const images = await pending
      if (current !== request.current) return
      setResults(images)
      setStatus(
        images.length ? "" : "No pictures found. Try a different search."
      )
    } catch (error) {
      if (current === request.current) setStatus(errorMessage(error))
    }
  }, [])

  const cancel = useCallback(() => {
    request.current++
  }, [])

  useEffect(() => {
    if (startingQuery.trim()) void search(startingQuery)
    return cancel
  }, [startingQuery, search, cancel])

  function changeQuery(value: string) {
    setQuery(value)
    request.current++
    setResults([])
    setStatus("")
  }

  function hideBrokenImage(url: string) {
    setResults((current) => current.filter((item) => item.image_url !== url))
    setStatus("If pictures don't load, try a different search.")
  }

  return {
    query,
    changeQuery,
    results: results.slice(0, visibleCount),
    status,
    search: () => search(query),
    hideBrokenImage,
    hasMore: visibleCount < results.length,
    showMore: () => setVisibleCount((count) => count + PAGE_SIZE),
  }
}
